import { ViewProps, Navigate, DateLocalizer, Views } from "react-big-calendar";
import { Box, Divider, Grid, Paper, Typography } from "@mui/material";
import { format, addMonths, isWithinInterval } from "date-fns";
import pt from "date-fns/locale/pt";
import { CalendarItem, CalendarItems } from "../calendar.types";
import { capitalize } from "lodash";
import { ServiceIcon } from "../../../settings-screens/schedules/components/ScheduleCustomEvent";
import "./overrides.css";
import {
    getDayColor,
    getDayEvent,
    EVENT_CONFIGS,
    shouldRenderBackground,
    BACKGROUND_RENDER_ORDER,
    DayEvents,
    getIconColor,
} from "./utils";
import { SessionTooltip } from "./SessionTooltip";
import { useTherapistTimeOff, getTimeOffsForDay, TimeOffItem } from "./useTherapistTimeOff";
import { TimeOffIcon } from "./TimeOffIcon";

type MonthCalendarProps = {
    month: Date;
    events?: CalendarItems;
    timeOffItems: Array<TimeOffItem>;
    onSelectDay: (day: Date) => void;
};

type EventBoxProps = {
    event: CalendarItem;
    psychologyEvent?: CalendarItem;
    neurofeedback?: CalendarItem;
    eeg?: CalendarItem;
    report?: CalendarItem;
};

const getYearMonths = (date: Date, { localizer }: { localizer: DateLocalizer }) => {
    const year = date.getFullYear();
    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31);
    const months: Array<Date> = [];

    let current = start;
    while (isWithinInterval(current, { start, end })) {
        months.push(new Date(current));
        current = addMonths(current, 1);
    }

    return months;
};

const CustomYearViewComponent = ({ date, localizer, onView, onNavigate, events }: ViewProps) => {
    const months = getYearMonths(new Date(date), { localizer });
    const year = new Date(date).getFullYear();
    const { timeOffItems } = useTherapistTimeOff(year);

    return (
        <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 3, lg: 3 }}>
            {months.map((month, index) => (
                <Grid item xs={1} key={index}>
                    <MonthCalendar
                        month={month}
                        //@ts-ignore
                        events={events}
                        timeOffItems={timeOffItems}
                        onSelectDay={(day) => {
                            onView?.(Views.DAY);
                            onNavigate(day);
                        }}
                    />
                </Grid>
            ))}
        </Grid>
    );
};

const CustomYearView = Object.assign(CustomYearViewComponent, {
    range: getYearMonths,
    navigate: (date: Date, action: any, { localizer }: { localizer: DateLocalizer }) => {
        if (action instanceof Date) {
            return action;
        }

        switch (action) {
            case Navigate.NEXT:
                return new Date(date.getFullYear() + 1, 0, 1);
            case Navigate.PREVIOUS:
                return new Date(date.getFullYear() - 1, 0, 1);
            default:
                return date;
        }
    },
    title: (date: Date) => {
        return `Ano ${date.getFullYear()}`;
    },
});

export default CustomYearView;

const MonthCalendar = ({ month, events, timeOffItems, onSelectDay }: MonthCalendarProps) => {
    // Get all days in the month
    const startDate = new Date(month.getFullYear(), month.getMonth(), 1);
    const endDate = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const days: Array<Date | null> = [];

    // Add empty slots for days before the first day of the month
    const firstDayWeekday = startDate.getDay() === 0 ? 6 : startDate.getDay() - 1;
    for (let i = 0; i < firstDayWeekday; i++) {
        days.push(null);
    }

    // Add the actual days
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        days.push(new Date(d));
    }

    // Weekday headers in Portuguese, starting with Monday
    const weekDays = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

    return (
        <Paper elevation={3} sx={{ p: 2, height: "400px", borderRadius: 2 }}>
            <Typography variant="subtitle1" style={{ textAlign: "center" }} color="primary">
                {capitalize(format(month, "MMMM", { locale: pt }))}
            </Typography>
            <Divider sx={{ mb: 4, mt: 3, ml: 4, mr: 4 }} />
            <Grid container columns={7}>
                {/* Render weekday headers */}
                {weekDays.map((day, idx) => (
                    <Grid item xs={1} key={`header-${idx}`}>
                        <Box
                            sx={{
                                p: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography variant="caption" color="primary">
                                {day}
                            </Typography>
                        </Box>
                    </Grid>
                ))}

                {/* Render calendar days */}
                {days.map((day, idx) => {
                    const slotHeight = 50;
                    if (!day)
                        return (
                            <Grid item xs={1} key={`empty-${idx}`}>
                                <Box sx={{ p: 1, height: slotHeight, border: "0.1px dashed #e0e0e0" }} />
                            </Grid>
                        );

                    const dayEvents = getDayEvent(day, events);
                    const dayTimeOffs = getTimeOffsForDay(timeOffItems, day);
                    const backgroundColor = getBackgroundColor(dayEvents);

                    return (
                        <Grid item xs={1} key={idx}>
                            <Box
                                sx={{
                                    position: "relative",
                                    p: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: slotHeight,
                                    border: "0.2px dashed #e0e0e0",
                                }}
                            >
                                {/* Render backgrounds */}
                                {BACKGROUND_RENDER_ORDER.map((eventType) => {
                                    const event = dayEvents[eventType];
                                    if (event && shouldRenderBackground(eventType, dayEvents)) {
                                        return (
                                            <BackgroundBox
                                                key={eventType}
                                                event={event}
                                                psychologyEvent={dayEvents.psychologyEvent}
                                                neurofeedback={dayEvents.neurofeedback}
                                                eeg={dayEvents.eeg}
                                                report={dayEvents.report}
                                            />
                                        );
                                    }
                                    return null;
                                })}

                                {/* Render all icons */}
                                {EVENT_CONFIGS.map((config) => {
                                    const event = dayEvents[config.type];
                                    const hasTimeOff = dayTimeOffs.length > 0;
                                    const isPsychologyEvent = config.type === "psychologyEvent";
                                    const shouldSkipForTimeOff = isPsychologyEvent && hasTimeOff;
                                    
                                    if (config.condition(dayEvents) && !shouldSkipForTimeOff) {
                                        return (
                                            <ServiceIconWrapper
                                                key={config.type}
                                                event={event!}
                                                position={config.position}
                                                dayEvents={dayEvents}
                                            />
                                        );
                                    }
                                    return null;
                                })}

                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        position: "relative",
                                    }}
                                >
                                    {format(day, "d")}
                                </Typography>

                                {/* Render time-off icon */}
                                <TimeOffIcon timeOffs={dayTimeOffs} backgroundColor={backgroundColor} />
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>
        </Paper>
    );
};

const BackgroundBox = ({ event, psychologyEvent, neurofeedback }: EventBoxProps) => {
    const getBackground = () => {
        const color = getDayColor(event);
        const category = event.service?.category;

        if (!category) {
            return "transparent";
        }

        // Only Psychology and Neurofeedback can affect the background
        if (category === "psicologia" && neurofeedback) {
            return `linear-gradient(to bottom right, ${color} 50%, ${getDayColor(neurofeedback)} 50%)`;
        } else if (category === "neurofeedback" && psychologyEvent) {
            return `linear-gradient(to bottom right, ${getDayColor(psychologyEvent)} 50%, ${color} 50%)`;
        } else if (category === "psicologia" || category === "neurofeedback") {
            return color;
        } else if (!psychologyEvent && !neurofeedback) {
            // If it's a standalone service (not psychology or neurofeedback)
            return color;
        }

        return "transparent";
    };

    return (
        <Box
            sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: getBackground(),
            }}
        />
    );
};

const SERVICES_WITH_TIME = [33, 34]; // neurofeedback: 33, psicologia: 34

const getBackgroundColor = (dayEvents: DayEvents) => {
    const { psychologyEvent, neurofeedback, eeg, report } = dayEvents;
    
    if (psychologyEvent) {
        return getDayColor(psychologyEvent);
    }
    if (neurofeedback) {
        return getDayColor(neurofeedback);
    }
    if (eeg) {
        return getDayColor(eeg);
    }
    if (report) {
        return getDayColor(report);
    }
    
    return "#ffffff";
};

const ServiceIconWrapper = ({
    event,
    position,
    dayEvents,
}: {
    event: CalendarItem;
    position: { top?: number; bottom?: number; left?: number; right?: number };
    dayEvents: DayEvents;
}) => {
    const iconColor = getIconColor(event, dayEvents);
    const sessionTime = format(event.start, "HH:mm");
    const isIconOnLeft = position.left !== undefined;
    const shouldShowTime = SERVICES_WITH_TIME.includes(event.service?.id ?? 0);

    return (
        <SessionTooltip event={event}>
            <Box
                sx={{
                    position: "absolute",
                    ...position,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    flexDirection: isIconOnLeft ? "row" : "row-reverse",
                    gap: "2px",
                }}
            >
                <ServiceIcon
                    service_id={event.service?.id}
                    styles={{
                        fontSize: "1rem",
                        color: iconColor,
                    }}
                />
                {shouldShowTime && (
                    <Typography
                        sx={{
                            fontSize: "0.6rem",
                            color: iconColor,
                            lineHeight: 1,
                        }}
                    >
                        {sessionTime}
                    </Typography>
                )}
            </Box>
        </SessionTooltip>
    );
};

